'use client'

import { Cell, Icon, Image, Spacer, Text } from '@vinted/web-ui'

import { Clock16, LocationPin16 } from '@vinted/monochrome-icons'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  DropOffPointModel,
  DropOffPointCarrierModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

enum DropOffPointStatus {
  Open = 'open',
  Closed = 'closed',
  Default = 'default',
}

type Props = {
  dropOffPoint?: DropOffPointModel
  dropOffPointCarrier?: DropOffPointCarrierModel | null
  navigating?: boolean
  chevron?: boolean
  onClick?: () => void
}

const DropOffPointInfoCard = ({
  dropOffPoint,
  dropOffPointCarrier,
  navigating,
  chevron,
  onClick,
}: Props) => {
  const renderLockerTypeSection = () => (
    <Cell
      styling={Cell.Styling.Tight}
      theme="transparent"
      prefix={
        <Image
          src={dropOffPoint?.descriptionIconUrl}
          fallbackSrc=""
          size={Image.Size.Small}
          alt=""
        />
      }
      body={
        <div className="u-flexbox u-align-items-center u-gap-medium">
          <Text
            as="h3"
            text={dropOffPoint?.name}
            type={Text.Type.Subtitle}
            testId="drop-off-point-locker-type"
          />
        </div>
      }
    />
  )

  const renderDropOffPointSection = (
    icon: ComponentProps<typeof Icon>['name'],
    text: string,
    theme?: ComponentProps<typeof Text>['theme'],
  ) => (
    <div className="u-flexbox u-gap-medium">
      <Icon name={icon} color={Icon.Color.GreyscaleLevel2} />
      <Text as="h3" text={text} type={Text.Type.Subtitle} theme={theme} />
    </div>
  )

  const getFieldTheme = (status: string) => {
    switch (status) {
      case DropOffPointStatus.Open:
        return 'success'
      case DropOffPointStatus.Closed:
        return 'warning'
      default:
        return undefined
    }
  }

  const renderDropOffPointStatus = () => {
    if (!dropOffPoint) return null

    const textTheme = getFieldTheme(dropOffPoint.openingStatus.status)

    return (
      <>
        <Spacer size={Spacer.Size.Small} />
        {renderDropOffPointSection(Clock16, dropOffPoint.openingStatus.text, textTheme)}
      </>
    )
  }

  const renderShippingPointInfo = () => {
    if (!dropOffPoint) return null

    const { dropOffPointAddress } = dropOffPoint

    return (
      <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
        {renderLockerTypeSection()}
        {renderDropOffPointSection(LocationPin16, dropOffPointAddress)}
      </SeparatedList>
    )
  }

  const renderCarrierInfo = () => (
    <Cell
      styling={Cell.Styling.Tight}
      theme="transparent"
      prefix={
        <Image src={dropOffPointCarrier?.iconUrl} fallbackSrc="" size={Image.Size.Regular} alt="" />
      }
      body={
        <div className="u-flexbox u-align-items-center u-gap-medium">
          <Text
            as="h2"
            text={dropOffPointCarrier?.name}
            type={Text.Type.Title}
            testId="drop-off-point-carrier-title"
          />
        </div>
      }
    />
  )

  return (
    <Cell
      title={renderCarrierInfo()}
      body={
        <>
          <Spacer size={Spacer.Size.Medium} />
          {renderShippingPointInfo()}
          {renderDropOffPointStatus()}
        </>
      }
      type={navigating ? Cell.Type.Navigating : undefined}
      onClick={onClick}
      testId="drop-off-point-info-card"
      chevron={chevron}
    />
  )
}

export default DropOffPointInfoCard
